import React, { useState } from "react";
import { FaRegUser } from "react-icons/fa";
import { MdOutlineMailOutline, MdOutlineLocationOn } from "react-icons/md";
import { GoArrowRight } from "react-icons/go";
import { BlackButton } from "../Buttons";
import Modal from "./modal/Modal";
import Edit from "./modal/Edit";
import EditPassword from "./modal/EditPassword";
import Subscription from "./modal/Subscription";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";

const UserProfile = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const handleEditPassword = (updatedUser) => setIsPasswordOpen(false);
  const handleEditUser = (updatedUser) => setIsEditOpen(false);
  const handleUpdateSubscription = (updatedUser) =>
    setIsSubscriptionOpen(false);

  return (
    <>
      <Helmet>
        <title>My Account</title>
        <meta name="description" content={`Welcome to My Account page`} />
      </Helmet>

      <div className="w-full px-2 sm:px-6 md:px-10 lg:px-16 py-6">
        <h1 className="text-center font-bold text-[1.6rem] sm:text-[1.7rem] md:text-[1.8rem] mb-6">
          My Account
        </h1>

        {/* Account Information */}
        <div className="mb-6">
          <h2 className="font-semibold text-[1.2rem] sm:text-[1.3rem] md:text-[1.4rem] mb-4">
            Account Information
          </h2>
          <div className="flex flex-col md:flex-row flex-wrap gap-4">
            {/* Contact Information Card */}
            <div className="flex-1 w-[100%] sm:w-[250px] py-6 px-4 shadow-[0_3px_8px_rgba(0,0,0,0.24)] bg-white rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 flex justify-center items-center bg-black rounded-full flex-shrink-0">
                <FaRegUser size={28} color="white" />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <h2 className="font-semibold text-[1.2rem]">
                  Contact Information
                </h2>
                <p className="text-[1rem]">{user?.name}</p>
                <p className="text-[1rem]">{user?.email}</p>
                <p className="text-[1rem]">{user?.phone}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <BlackButton
                    title="Edit"
                    handleClick={() => setIsEditOpen(true)}
                    icon={<GoArrowRight />}
                  />
                  <BlackButton
                    title="Change Password"
                    handleClick={() => setIsPasswordOpen(true)}
                    icon={<GoArrowRight />}
                  />
                </div>
              </div>
            </div>

            {/* Newsletter Card */}
            <div className="flex-1 min-w-[250px] py-6 px-4 shadow-[0_3px_8px_rgba(0,0,0,0.24)] bg-white rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 flex justify-center items-center bg-black rounded-full flex-shrink-0">
                <MdOutlineMailOutline size={28} color="white" />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <h2 className="font-semibold text-[1.2rem]">Newsletter</h2>
                <p className="text-[1rem]">
                  You aren't subscribed to our newsletter.
                </p>
                <div className="mt-2">
                  <BlackButton
                    title="Edit"
                    handleClick={() => setIsSubscriptionOpen(true)}
                    icon={<GoArrowRight />}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Book */}
        <div className="mb-6">
          <h2 className="font-semibold text-[1.4rem] sm:text-[1.5rem] mb-4">
            Address Book
          </h2>
          <div className="flex flex-col md:flex-row flex-wrap gap-4">
            {/* Billing Address */}
            <div className="flex-1 min-w-[250px] py-6 px-4 shadow-[0_3px_8px_rgba(0,0,0,0.24)] bg-white rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 flex justify-center items-center bg-black rounded-full flex-shrink-0">
                <MdOutlineLocationOn size={28} color="white" />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <h2 className="font-semibold text-[1.2rem]">
                  Default Billing Address
                </h2>
                <p className="text-[1rem]">
                  {user?.street_address_1 ||
                    "You have not set a default billing address."}
                </p>
                <div className="mt-2">
                  <BlackButton
                    title="Edit Address"
                    handleClick={() => navigate("/user/address")}
                    icon={<GoArrowRight />}
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="flex-1 min-w-[250px] py-6 px-4 shadow-[0_3px_8px_rgba(0,0,0,0.24)] bg-white rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 flex justify-center items-center bg-black rounded-full flex-shrink-0">
                <MdOutlineLocationOn size={28} color="white" />
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <h2 className="font-semibold text-[1.2rem]">
                  Default Shipping Address
                </h2>
                <p className="text-[1rem]">
                  {user?.street_address_2 ||
                    "You have not set a default shipping address."}
                </p>
                <div className="mt-2">
                  <BlackButton
                    title="Edit Address"
                    handleClick={() => navigate("/user/address")}
                    icon={<GoArrowRight />}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <Modal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          title="Edit User Info"
        >
          <Edit userData={user} onSave={handleEditUser} />
        </Modal>
        <Modal
          isOpen={isPasswordOpen}
          onClose={() => setIsPasswordOpen(false)}
          title="Change Password"
        >
          <EditPassword userData={user} onSave={handleEditPassword} />
        </Modal>
        <Modal
          isOpen={isSubscriptionOpen}
          onClose={() => setIsSubscriptionOpen(false)}
          title="Update User Subscription"
        >
          <Subscription userData={user} onSave={handleUpdateSubscription} />
        </Modal>
      </div>
    </>
  );
};

export default UserProfile;
